import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCliente, getDatiAzienda } from '../services/api';
import { Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import '../print.css'; // Importa il CSS di stampa

function ClientiPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [datiAzienda, setDatiAzienda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClienteAndAziendaData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [clienteResponse, aziendaResponse] = await Promise.all([
          getCliente(id),
          getDatiAzienda()
        ]);
        setCliente(clienteResponse.data);
        setDatiAzienda(aziendaResponse.data);
      } catch (err) {
        setError('Errore nel caricamento dei dati per la stampa.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchClienteAndAziendaData();
  }, [id]);

  useEffect(() => {
    const handleAfterPrint = () => {
      // Introduce a small delay to ensure the print dialog is fully dismissed
      setTimeout(() => {
        navigate('/clienti');
      }, 100);
    };

    if (!loading && !error && cliente && datiAzienda) {
      window.addEventListener('afterprint', handleAfterPrint);
      window.print();
    }

    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [loading, error, cliente, datiAzienda, navigate]);

  const renderPrintField = (label, value) => (
    <div className="print-field">
      <span className="label">{label}:</span>
      <span className="value">{value || 'N/A'}</span>
    </div>
  );

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p>Preparazione stampa cliente...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <p>Impossibile preparare la stampa.</p>
      </Container>
    );
  }

  if (!cliente || !datiAzienda) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Dati del cliente o dell'azienda non trovati per la stampa.</Alert>
      </Container>
    );
  }

  return (
    <div id="printable-area" className="portrait-page">
      <div className="print-container">
        <header className="print-header">
          <div className="company-details">
            {datiAzienda.logo_url && (
              <img
                src={`${process.env.REACT_APP_STATIC_FILES_BASE_URL}${datiAzienda.logo_url}`}
                alt="Logo Azienda"
                style={{ maxHeight: '150px', maxWidth: '150px', marginRight: '20px', objectFit: 'contain' }}
              />
            )}
          </div>
          <div className="print-info">
            <p><strong>Data di Stampa:</strong> {new Date().toLocaleDateString()}</p>
            <p><strong>ID Cliente:</strong> {cliente.id}</p>
          </div>
        </header>

        <main>
          <h2 className="document-title text-center">Scheda Cliente</h2>
          <h2 className="document-title text-center">{cliente.nome} {cliente.cognome}</h2>

          {/* Dati Anagrafici */}
          <section className="print-section">
            <div className="print-section-title">Dati Anagrafici</div>
            <div className="print-section-content">
              <Row>
                <Col xs={6}>
                  {renderPrintField('ID', cliente.id)}
                  {renderPrintField('Nome', cliente.nome)}
                  {renderPrintField('Cognome', cliente.cognome)}
                  {renderPrintField('Luogo di Nascita', cliente.luogo_nascita)}
                </Col>
                <Col xs={6}>
                  {renderPrintField('Data di Nascita', cliente.data_nascita ? new Date(cliente.data_nascita).toLocaleDateString() : 'N/A')}
                  {renderPrintField('Codice Fiscale', cliente.codice_fiscale)}
                  {renderPrintField('Partita IVA', cliente.partita_iva)}
                </Col>
              </Row>
            </div>
          </section>

          {/* Indirizzo */}
          <section className="print-section">
            <div className="print-section-title">Indirizzo</div>
            <div className="print-section-content">
              <Row>
                <Col xs={6}>
                  {renderPrintField('Indirizzo', cliente.indirizzo)}
                  {renderPrintField('CAP', cliente.cap)}
                </Col>
                <Col xs={6}>
                  {renderPrintField('Città', cliente.citta)}
                  {renderPrintField('Provincia', cliente.provincia)}
                  {renderPrintField('Nazione', cliente.nazione)}
                </Col>
              </Row>
            </div>
          </section>

          {/* Contatti */}
          <section className="print-section">
            <div className="print-section-title">Contatti</div>
            <div className="print-section-content">
              <Row>
                <Col xs={6}>
                  {renderPrintField('Email', cliente.email)}
                  {renderPrintField('PEC', cliente.pec)}
                  {renderPrintField('Codice Destinatario', cliente.codice_destinatario)}
                </Col>
                <Col xs={6}>
                  {renderPrintField('Telefono', cliente.telefono)}
                  {renderPrintField('Cellulare', cliente.cellulare)}
                </Col>
              </Row>
            </div>
          </section>

          {/* Altri Dati */}
          <section className="print-section">
            <div className="print-section-title">Altri Dati</div>
            <div className="print-section-content">
              <Row>
                <Col xs={6}>
                  {renderPrintField('Punti Fedeltà', cliente.punti_fedelta)}
                  {renderPrintField('Origine', cliente.origine)}
                </Col>
              </Row>
            </div>
          </section>
        </main>

        <footer className="print-footer">
          <p>{datiAzienda.denominazione} - {datiAzienda.indirizzo} - {datiAzienda.cap} {datiAzienda.citta} ({datiAzienda.provincia}) - P.IVA {datiAzienda.piva}</p>
          <p>Email: {datiAzienda.email} - Tel: {datiAzienda.tel}</p>
        </footer>
      </div>
    </div>
  );
}

export default ClientiPrint;
